import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Radio,
  Input,
  Button,
  Rate,
  Checkbox,
  Space,
  Slider,
  Row,
  Col,
} from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SurveyApi from '../../Api/SurveyApi';
import SurveyReportApi from '../../Api/SurveyReportApi';
import { useAuth } from '../../Context/AuthContext';
import { toastError, toastSuccess } from '../../Components/Toast/ToastComponent';
import BackButton from '../ReusableComponents/BackButton';

const { Text, Title } = Typography;

const MysurveyQuestions = (props) => {
  const { authState } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const numericId = props.modalSurveyId ? Number(props.modalSurveyId) : (id ? Number(id) : null);
  const targetUserId = props.modalUserId || authState?.id;
  const passedQuestions = location.state?.allQuestions || [];

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [surveyQuestions, setSurveyQuestions] = useState([]);

  const isCompleted = props.isModal ? props.isCompleted : (survey?.completion_status === 'Completed' || location.state?.status === 'Completed');

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const res = await SurveyApi.getById(numericId);
      if (res?.data?.success) {
        const surveyData = res.data.result?.rows?.[0] || res.data.result;
        if (surveyData) {
          setSurvey(surveyData);
          if (passedQuestions.length === 0 && surveyData.question) {
            const allQs = surveyData.question.flatMap(
              (cat) => cat.questions || [],
            );
            setSurveyQuestions(allQs);
          }
        }
      }
    } catch (err) {
      toastError('Connection Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurvey();
  }, [numericId]);

  useEffect(() => {
    if (passedQuestions.length > 0) {
      setSurveyQuestions(passedQuestions);
    }
  }, [passedQuestions]);

  const fetchUserAnswers = async () => {
    try {
      const response = await SurveyReportApi.getUserSurveyAnswers({
        survey_id: numericId,
        user_id: targetUserId,
        limit: 100000,
        offset: 0,
      });
      if (response?.data?.success) {
        const resultPayload = response.data.result || [];
        const flatAnswers = [];
        const flatten = (payload) => {
          if (!payload) return;
          if (Array.isArray(payload)) {
             payload.forEach(flatten);
          } else if (typeof payload === 'object') {
             if (payload.question && payload.answer !== undefined) {
               flatAnswers.push({ question: payload.question, answer: payload.answer });
             } else {
               ['answers','answer','responses','response','rows','result','data'].forEach(k => {
                  if (payload[k]) flatten(payload[k]);
               });
             }
          }
        };
        flatten(resultPayload);

        const mappedAnswers = {};
        const usedAnswerIndices = new Set();

        for (let i = 0; i < surveyQuestions.length; i++) {
          const qItem = surveyQuestions[i];
          const qTitle = qItem.title;
          
          // Use findIndex and a Set to handle duplicate question titles correctly
          const foundIdx = flatAnswers.findIndex((f, idx) => f.question === qTitle && !usedAnswerIndices.has(idx));
          
          if (foundIdx !== -1) {
             usedAnswerIndices.add(foundIdx);
             const found = flatAnswers[foundIdx];
             let parsedAnswer = found.answer;
             try {
                if (typeof parsedAnswer === 'string' && (parsedAnswer.startsWith('{') || parsedAnswer.startsWith('['))) {
                   parsedAnswer = JSON.parse(parsedAnswer);
                }
             } catch(e) {}

             const type = (qItem.likert_scale?.question_type || qItem.likert_scale?.type || '').toLowerCase();
             if (type === 'matrix' && typeof parsedAnswer === 'object' && parsedAnswer !== null) {
                const _likert = qItem.likert_scale || {};
                const _nestedOpts = _likert.options && !Array.isArray(_likert.options) ? _likert.options : null;
                const rowsObj = _nestedOpts ? (_nestedOpts.rows || []) : (_likert.rows || []);
                
                if (rowsObj && rowsObj.length > 0) {
                    const rebuiltObj = {};
                    rowsObj.forEach((row, rIdx) => {
                       const rowTitle = typeof row === 'object' ? row.title : row;
                       rebuiltObj[rIdx] = parsedAnswer[rowTitle] || '';
                    });
                    mappedAnswers[`0-${i}`] = rebuiltObj;
                } else {
                    const rowTitle = qItem.likert_scale?.question || qItem.title;
                    mappedAnswers[`0-${i}`] = { 0: parsedAnswer[rowTitle] || '' };
                }
             } else {
                mappedAnswers[`0-${i}`] = parsedAnswer;
             }
          }
        }
        setAnswers(mappedAnswers);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (isCompleted && surveyQuestions.length > 0 && targetUserId) {
      fetchUserAnswers();
    }
  }, [isCompleted, surveyQuestions, targetUserId]);

  const handleAnswerChange = (catIdx, qIdx, value, isMatrix = false, rowIdx = null) => {
    setAnswers((prev) => {
      const key = `${catIdx}-${qIdx}`;
      if (isMatrix) {
        const currentMatrix = prev[key] || {};
        return { ...prev, [key]: { ...currentMatrix, [rowIdx]: value } };
      }
      return { ...prev, [key]: value };
    });
  };



  const handleSubmit = async () => {
    if (!survey || !survey.question) {
      toastError('Survey data not loaded properly');
      return;
    }

    // ── Validation ──────────────────────────────────────────────────────────
    // Uses the same flat surveyQuestions index (i) and catIdx=0 that
    // handleAnswerChange / renderQuestions use — so keys always match.
    let hasError = false;
    let missedQuestionIdx = null;
    for (let i = 0; i < surveyQuestions.length; i++) {
      const qItem = surveyQuestions[i];
      const type = (
        qItem.likert_scale?.question_type ||
        qItem.likert_scale?.type ||
        ''
      ).toLowerCase();

      if (type === 'matrix') {
        const _likert = qItem.likert_scale || {};
        const _nested = _likert.options && !Array.isArray(_likert.options) ? _likert.options : null;
        const rowsObj = _nested ? (_nested.rows || []) : (_likert.rows || []);

        if (rowsObj && rowsObj.length > 0) {
          // Format 2 (new): single question with embedded rows
          if (qItem.required || qItem.is_required) {
            const matrixValue = answers[`0-${i}`] || {};
            for (let r = 0; r < rowsObj.length; r++) {
              if (!matrixValue[r]) { hasError = true; missedQuestionIdx = i; break; }
            }
          }
          if (hasError) break;
        } else {
          // Format 1 (old): grouped questions sharing a title
          const currentTitle = qItem.title;
          let j = i;
          let groupRequired = false;
          while (
            j < surveyQuestions.length &&
            surveyQuestions[j].title === currentTitle &&
            (
              surveyQuestions[j].likert_scale?.question_type?.toLowerCase() === 'matrix' ||
              surveyQuestions[j].likert_scale?.type?.toLowerCase() === 'matrix'
            )
          ) {
            if (surveyQuestions[j].required || surveyQuestions[j].is_required) groupRequired = true;
            j++;
          }
          if (groupRequired) {
            for (let k = i; k < j; k++) {
              const matrixValue = answers[`0-${k}`];
              if (!matrixValue || !matrixValue[0]) { hasError = true; missedQuestionIdx = i; break; }
            }
          }
          if (hasError) break;
          i = j - 1; // skip past the grouped rows
        }
      } else {
        if (qItem.required || qItem.is_required) {
          const val = answers[`0-${i}`];
          if (val === undefined || val === null || val === '' || (typeof val === 'string' && val.trim() === '') || (Array.isArray(val) && val.length === 0)) {
            hasError = true;
            missedQuestionIdx = i;
            break;
          }
        }
      }
    }

    if (hasError) {
      toastError('Please answer all required questions before submitting.');
      
      const errEl = document.getElementById(`survey-q-${missedQuestionIdx}`);
      if (errEl) errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      setSubmitting(true);

      // ── Build payload ────────────────────────────────────────────────────
      // We iterate surveyQuestions (flat) to resolve answers, then bucket
      // them back into their category groups — mirroring the render exactly.
      let flatIdx = 0; // mirrors the render's `i` (flat surveyQuestions index)

      const formattedAnswers = survey.question.map((cat) => ({
        category_id: Number(cat.id || cat.category_id),
        responses: (cat.questions || []).map((qItem) => {
          const answerKey = `0-${flatIdx}`;
          const answerValue = answers[answerKey];
          const type = (qItem.likert_scale?.question_type || qItem.likert_scale?.type || '').toLowerCase();

          let formattedAnswer;
          if (type === 'matrix') {
            const _likert = qItem.likert_scale || {};
            const _nestedOpts = _likert.options && !Array.isArray(_likert.options) ? _likert.options : null;
            const rowsObj = _nestedOpts ? (_nestedOpts.rows || []) : (_likert.rows || []);

            // Build { "Row Title": "Selected Column", ... }
            const matrixAnswer = {};
            rowsObj.forEach((row, rIdx) => {
              const rowTitle = typeof row === 'object' ? row.title : row;
              // answerValue is { [rIdx]: "Column Title" } — filled by handleAnswerChange
              matrixAnswer[rowTitle] =
                (typeof answerValue === 'object' && answerValue !== null && !Array.isArray(answerValue))
                  ? (answerValue[rIdx] ?? '')
                  : '';
            });
            formattedAnswer = JSON.stringify(matrixAnswer);
          } else if (typeof answerValue === 'object' && answerValue !== null) {
            formattedAnswer = JSON.stringify(answerValue);
          } else {
            formattedAnswer = String(answerValue ?? '');
          }

          flatIdx++; // advance AFTER reading — next question gets next index

          return { question: qItem.title, answer: formattedAnswer };
        }),
      }));

      const payload = {
        surveyId: Number(survey.id),
        period_id: survey?.PeriodModel?.id || null,
        year: 2026,
        user_id: targetUserId,
        answers: formattedAnswers,
      };

      const res = await SurveyApi.submit(payload);
      if (res.data?.success) {
        toastSuccess('Survey submitted successfully!');
        navigate(-1);
      } else {
        toastError(res.data?.message || 'Submit failed');
      }
    } catch (err) {
      toastError('Connection Error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Added displayNum parameter to show question number
  const renderMatrixTable = (group, catIdx, startIdx, displayNum) => {
    const firstItem = group[0];
    const options = firstItem.likert_scale?.options || [];
    const isRequired = group.some(q => q.required || q.is_required);

    return (
      <div id={`survey-q-${startIdx}`} key={`matrix-${startIdx}`} style={{ marginBottom: 30, paddingBottom: 30, borderBottom: '1px solid #e0e0e0' }}>
        <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 15 }}>
          {displayNum}. {firstItem.title} {isRequired && <span style={{ color: 'red' }}>*</span>}
        </Text>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ width: '250px' }}></th>
                {options.map((col, i) => (
                  <th key={i} style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                    {typeof col === 'object' ? col.title : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.map((qItem, gIdx) => {
                const currentQIdx = startIdx + gIdx;
                const matrixValue = answers[`${catIdx}-${currentQIdx}`] || {};
                return (
                  <tr key={gIdx} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '15px 10px', color: '#444' }}>
                     {qItem.likert_scale?.question || qItem.title}
                    </td>
                    {options.map((col, colIdx) => {
                      const colValue = typeof col === 'object' ? col.title : col;
                      return (
                        <td key={colIdx} style={{ textAlign: 'center' }}>
                          <Radio
                            disabled={isCompleted}
                            checked={matrixValue[0] === colValue}
                            onChange={() => handleAnswerChange(catIdx, currentQIdx, colValue, true, 0)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMatrixTableNew = (qItem, catIdx, startIdx, displayNum) => {
    const likert = qItem.likert_scale || {};
    // Support both old format (likert.rows + likert.options[]) and new format (likert.options.rows + likert.options.columns)
    const nestedOptions = likert.options && !Array.isArray(likert.options) ? likert.options : null;
    const columns = nestedOptions ? (nestedOptions.columns || []) : (Array.isArray(likert.options) ? likert.options : []);
    const rows = nestedOptions ? (nestedOptions.rows || []) : (likert.rows || []);
    const isRequired = qItem.required || qItem.is_required;
    const matrixValue = answers[`${catIdx}-${startIdx}`] || {};

    return (
      <div id={`survey-q-${startIdx}`} key={`matrix-${startIdx}`} style={{ marginBottom: 30, paddingBottom: 30, borderBottom: '1px solid #e0e0e0' }}>
        <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 15 }}>
          {displayNum}. {qItem.title} {isRequired && <span style={{ color: 'red' }}>*</span>}
        </Text>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ width: '250px' }}></th>
                {columns.map((col, i) => (
                  <th key={i} style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                    {typeof col === 'object' ? col.title : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((rowItem, rIdx) => {
                return (
                  <tr key={rIdx} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '15px 10px', color: '#444' }}>
                      {typeof rowItem === 'object' ? rowItem.title : rowItem}
                    </td>
                    {columns.map((col, colIdx) => {
                      const colTitle = typeof col === 'object' ? col.title : col;
                      return (
                        <td key={colIdx} style={{ textAlign: 'center' }}>
                          <Radio 
                            disabled={isCompleted} 
                            checked={matrixValue[rIdx] === colTitle}
                            onChange={() => handleAnswerChange(catIdx, startIdx, colTitle, true, rIdx)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderInputType = (qItem, catIdx, qIdx) => {
    const likert = qItem.likert_scale || {};
    const options = likert.options || [];
    const type = (likert.question_type || likert.type || 'text').toLowerCase();
    const currentValue = answers[`${catIdx}-${qIdx}`];

    switch (type) {
      case 'checkbox':
        return (
          <Checkbox.Group
            disabled={isCompleted}
            value={currentValue || []}
            onChange={(vals) => handleAnswerChange(catIdx, qIdx, vals)}
          >
            <Space direction="vertical">
              {options.map((opt, i) => (
                <Checkbox key={i} value={opt.title}>{opt.title}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );

      case 'radio':
        return (
          <Radio.Group
            disabled={isCompleted}
            value={currentValue}
            onChange={(e) => handleAnswerChange(catIdx, qIdx, e.target.value)}
          >
            <Space direction="vertical">
              {options.map((opt, i) => (
                <Radio key={i} value={opt.title}>{opt.title}</Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'slider':
    case 'sliders': {
  const firstOpt = options?.[0] || {};

  const sliderMin = Number.isFinite(Number(firstOpt.min)) ? Number(firstOpt.min) : 0;
  const sliderMax = Number.isFinite(Number(firstOpt.max)) ? Number(firstOpt.max) : 100;

  const safeValue =
    currentValue !== undefined &&
    currentValue !== null &&
    !isNaN(Number(currentValue))
      ? Number(currentValue)
      : sliderMin;

  return (
    <div style={{ padding: '0 10px 20px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#888', minWidth: 20 }}>{sliderMin}</span>

        <Slider
          disabled={isCompleted}
          min={sliderMin}
          max={sliderMax}
          step={1}
          value={safeValue}
          onChange={(val) => handleAnswerChange(catIdx, qIdx, val)}
          tooltip={{ formatter: (val) => `${val}` }}
          style={{ flex: 1 }}
        />

        <span style={{ fontSize: 12, color: '#888', minWidth: 28 }}>{sliderMax}</span>
      </div>
    </div>
  );
}

      case 'rating': {
        const starCount = Number(options?.[0]?.title) || 5;
        return <Rate disabled={isCompleted} count={starCount} value={currentValue} onChange={(val) => handleAnswerChange(catIdx, qIdx, val)} />;
      }

      default:
        return (
          <Input.TextArea
            disabled={isCompleted}
            rows={3}
            placeholder="Type response..."
            value={currentValue || ''}
            onChange={(e) => handleAnswerChange(catIdx, qIdx, e.target.value)}
          />
        );
    }
  };

  const renderQuestions = () => {
    const rendered = [];
    let displayIdx = 1; // Tracks the visual question number
    
    for (let i = 0; i < surveyQuestions.length; i++) {
      const qItem = surveyQuestions[i];
      const type = (qItem.likert_scale?.question_type || qItem.likert_scale?.type || '').toLowerCase();

      if (type === 'matrix') {
        const likert = qItem.likert_scale || {};
        const nestedOpts = likert.options && !Array.isArray(likert.options) ? likert.options : null;
        const rowsObj = nestedOpts ? (nestedOpts.rows || []) : (likert.rows || []);
        if (rowsObj && rowsObj.length > 0) {
           rendered.push(renderMatrixTableNew(qItem, 0, i, displayIdx));
           displayIdx++;
        } else {
           const group = [];
           const currentTitle = qItem.title;
           let j = i;
           
           while (j < surveyQuestions.length && 
                  surveyQuestions[j].title === currentTitle && 
                  (surveyQuestions[j].likert_scale?.question_type?.toLowerCase() === 'matrix' || surveyQuestions[j].likert_scale?.type?.toLowerCase() === 'matrix')) {
             group.push(surveyQuestions[j]);
             j++;
           }
           rendered.push(renderMatrixTable(group, 0, i, displayIdx));
           displayIdx++;
           i = j - 1;
        }
      } else {
        rendered.push(
          <div id={`survey-q-${i}`} key={i} style={{ marginBottom: 30, paddingBottom: 30, borderBottom: i < surveyQuestions.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
            <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 20 }}>
              {displayIdx}. {qItem.title} {(qItem.required || qItem.is_required) && <span style={{ color: 'red' }}>*</span>}
            </Text>
            {renderInputType(qItem, 0, i)}
          </div>
        );
        displayIdx++; // Increment for standard question
      }
    }
    return rendered;
  };

  return (
    <div style={props.isModal ? { padding: '0px' } : { padding: '20px', minHeight: '100vh', backgroundColor: '#f9f9f9' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {!props.isModal && (
          <Card style={{ marginBottom: 20, borderRadius: '4px', border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BackButton />
              <Title level={4} style={{ textAlign: 'center', margin: '0 auto' }}>
                {survey?.title || 'Survey Details'}
              </Title>
            </div>
          </Card>
        )}

        <Card style={{ marginBottom: 20, borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '20px' }}>
          {renderQuestions()}
        </Card>

        <Card style={{ marginTop: 20, borderRadius: '4px', border: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Button size="middle" onClick={() => props.onCancel ? props.onCancel() : navigate(-1)} style={{ width: 120 }}>{props.isModal ? 'Close' : 'Cancel'}</Button>
            </Col>
            {!isCompleted && (
              <Col>
                <Button 
                  type="primary" 
                  size="middle" 
                  loading={submitting} 
                  onClick={handleSubmit} 
                  style={{ width: 120 }}
                >
                  Submit
                </Button>
              </Col>
            )}
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default MysurveyQuestions;